import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Input, Divider, Alert } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import {
	signIn,
	showLoading,
	showAuthMessage,
	hideAuthMessage,
} from 'store/slices/authSlice';
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from "framer-motion"
import ReCAPTCHA from 'react-google-recaptcha';
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';
import OTPForm from './OTPForm';
import { ErrorExtractor } from '../CustomUtils.js/utils';

export const LoginForm = props => {
	const [invalidCount, setInvalidCount] = useState(4)
	const { api } = useMyContext()
	const navigate = useNavigate();
	//showAuthMessage = false
	const {
		showForgetPassword,
		hideAuthMessage,
		onForgetPasswordClick,
		signIn,
		token,
		redirect,
		showMessage,
		message,
		allowRedirect = true,
	} = props

	const initialCredential = {
		email: '',
		password: ''
	}

	const [showAlertMessage, setShowAlertMessage] = useState(false);
	const [captchaValue, setCaptchaValue] = useState(null);
	const [disabled, setDisabled] = useState(true);
	const [invalidEmail, setInvalidEmail] = useState(false);
	const [alertType, setAlertType] = useState();
	const [alertMessage, setAlertMessage] = useState(message);
	const [OTPState, setOTPState] = useState(false);

	const [otp, setOTP] = useState('');
	const [email, setEmail] = useState('');
	const [invalidEmailValue, setInvalidEmailValue] = useState('');
	const [password, setPassword] = useState('');
	const [timerVisible, setTimerVisible] = useState(false);
	const [waiting, setWaiting] = useState(false);
	const [countdown, setCountdown] = useState(30);
	const [sessionKey, setSessionKey] = useState(30);


	useEffect(() => {
		if (showMessage && message) {
			setShowAlertMessage(true)
			setAlertMessage(message)
		}
	}, [message, showMessage]);

	const onCaptchaChange = (value) => {
		if (value) {
			setCaptchaValue(value);
			setDisabled(false);
		}
	}

	const CheckTwoFactor = async (value) => {
		try {
			setWaiting(true)
			const email = value?.email;
			const password = value?.password;
			setEmail(email);
			setPassword(email);
			if (!email) setAlertMessage("Email is required");
			if (email === invalidEmailValue) {
				setShowAlertMessage(true);
				setAlertMessage('Please use a different email address. This one is not registered.');
				return;
			}
			const response = await apiClient.post(`${api}user-auth-authenticate`, {
				email, password
			});

			const isTwoFactor = response.data?.two_fector_auth
			const session_key = response.data?.session_key
			setSessionKey(session_key)
			if (isTwoFactor) {
				setWaiting(false)
				setTimerVisible(true);
				setOTPState(isTwoFactor)
			} else {
				onLogin(email, password, session_key)
			}
		} catch (error) {
			const err = ErrorExtractor(error);
			setShowAlertMessage(true);
			if (error.response?.data?.emailError) {
				setInvalidEmailValue(email);
				setAlertMessage('Email is not registered');
				setInvalidEmail(true);
			} else if ((error.response?.data?.code || err?.code) === 'WP') {
				setAlertMessage("Email and password do not match, please try again.");
				setInvalidCount(invalidCount - 1);
			} else {
				setAlertMessage(err);
			}
			console.log("Error during authentication:", error.response?.data?.code || err?.code);
		} finally {
			setWaiting(false)
		}
	};

	const HandleVerifyOTP = async () => {
		try {
			setWaiting(true)
			const response = await apiClient.post(`${api}verify-otp/${email}/${otp}`);
			if (response?.data?.status) {
				onLogin(email, password, sessionKey)
			}
		} catch (error) {
			const err = ErrorExtractor(error);
			setShowAlertMessage(true)
			setAlertMessage(err);
			return null; // Handle errors gracefully
		} finally {
			setWaiting(false)
		}
	}

	const onLogin = async (email, password, sessionKey) => {
		try {
			const data = await signIn({ email, password, sessionKey });
			if (data?.payload === "Wrong password") {
				setInvalidCount(invalidCount - 1);
			}
		} catch (error) {
			const err = error.response?.data?.error || error.response?.data?.message || "An error occurred during login";
			setShowAlertMessage(true);
			setAlertMessage(err);
		} finally {
			setWaiting(false)
		}
	};

	const location = useLocation();
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const status = params.get('status');
		const message = params.get('message');
		if (status && message) {
			setAlertType('success')
			setShowAlertMessage(true)
			setAlertMessage(decodeURIComponent(message))
			navigate(location.pathname, { replace: true });
		}
	}, [location, navigate]);

	useEffect(() => {
		if (token !== null && allowRedirect) {
			navigate(redirect)
		}
		if (showMessage) {
			const timer = setTimeout(() => hideAuthMessage(), 3000)
			return () => {
				clearTimeout(timer);
			};
		}
	}, []);
	useEffect(() => {
		let timer;
		if (timerVisible && countdown > 0) {
			timer = setInterval(() => {
				setCountdown(prevCountdown => prevCountdown - 1);
			}, 1000);
		}

		return () => clearInterval(timer);
	}, [timerVisible, countdown]);

	useEffect(() => {
		if (countdown === 0) {
			setTimerVisible(false);
		}
	}, [countdown]);

	const handleChange = (value) => {
		setOTP(value.join(''));
	}

	const otpMessage = 'OTP sent to your registered mobile and email';
	const handleEmailChange = (e) => {
		const currentEmail = e.target.value;
		setShowAlertMessage(false);
		if (invalidEmail) {
			// Only clear error state if the email is actually different
			if (currentEmail !== invalidEmailValue) {
				setInvalidEmail(false);
				setShowAlertMessage(false);
			} else {
				// If they're trying the same invalid email again, remind them
				setShowAlertMessage(true);
				setAlertMessage('Please use a different email address. This one is not registered.');
			}
		}
	};

	return (
		<>
			<motion.div
				initial={{ opacity: 0, marginBottom: 0 }}
				animate={{
					opacity: showAlertMessage ? 1 : 0,
					marginBottom: showAlertMessage ? 20 : 0
				}}
			>
				<Alert type={`${alertType === 'success' ? 'success' : 'error'}`} showIcon message={alertMessage} closable></Alert>
			</motion.div>
			{invalidCount > 0 && invalidCount <= 3 && <Alert showIcon closable message={`Attempt left : ${invalidCount}`} type="warning" className='mb-3' />}

			{OTPState ?
				<OTPForm
					HandleVerifyOTP={HandleVerifyOTP}
					handleChange={handleChange}
					initialCredential={initialCredential}
					otpMessage={otpMessage}
					waiting={waiting}
					timerVisible={timerVisible}
					countdown={countdown}
					CheckTwoFactor={CheckTwoFactor}
				/>
				:
				<Form
					layout="vertical"
					name="login-form"
					initialValues={initialCredential}
					onFinish={CheckTwoFactor}
				>
					<Form.Item
						name="email"
						label="Email"
						validateStatus={invalidEmail ? 'error' : undefined}
						status={invalidEmail ? 'error' : ''}
						rules={[
							{
								required: true,
								message: 'Please input your email',
							},
							{
								type: 'email',
								message: 'Please enter a validate email!'
							}
						]}>
						<Input onChange={handleEmailChange} type='email' prefix={<MailOutlined className="text-primary" />} />
					</Form.Item>
					<Form.Item
						name="password"
						label={
							<div className={`${showForgetPassword ? 'd-flex justify-content-between w-100 align-items-center' : ''}`}>
								<span>Password</span>
								{
									showForgetPassword &&
									<span
										onClick={() => onForgetPasswordClick}
										className="cursor-pointer font-size-sm font-weight-normal text-muted"
									>
										Forgot Passwords?
									</span>
								}
							</div>
						}
						rules={[
							{
								required: true,
								message: 'Please input your password',
							}
						]}
					>
						<Input.Password prefix={<LockOutlined className="text-primary" />} />
					</Form.Item>
					<Form.Item className='text-center'>
						<ReCAPTCHA
							sitekey="6Ldj2fAiAAAAANPd1Uk2SzE0I_Jp_7syjxbjPHTm"
							onChange={onCaptchaChange}
						/>
					</Form.Item>
					<Form.Item>
						<Button type="primary" htmlType="submit"
							block
							disabled={disabled || invalidEmail}
							loading={waiting}
						>
							Sign In
						</Button>
					</Form.Item>
					<Divider>
						<span className="text-muted font-size-base font-weight-normal"><Link to="/forgot-password">Forgot Passwordssss?</Link></span>
					</Divider>

				</Form>
			}
		</>
	)
}



LoginForm.propTypes = {
	otherSignIn: PropTypes.bool,
	showForgetPassword: PropTypes.bool,
	extra: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.element
	]),
};

LoginForm.defaultProps = {
	otherSignIn: true,
	showForgetPassword: false
};

const mapStateToProps = ({ auth }) => {
	const { loading, message, showMessage, token, redirect } = auth;
	return { loading, message, showMessage, token, redirect }
}

const mapDispatchToProps = {
	signIn,
	showAuthMessage,
	showLoading,
	hideAuthMessage,
	// signInWithGoogle,
	// signInWithFacebook
}

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm)
